import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  id: string;
  label: string;
  path: string;
  icon: string;
}

const menuItems: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/', icon: 'ri-dashboard-line' },
  { id: 'rezervasyonlar', label: 'Rezervasyonlar', path: '/rezervasyonlar', icon: 'ri-calendar-check-line' },
  { id: 'bugun', label: 'Bugünkü Transferler', path: '/bugun', icon: 'ri-taxi-line' },
  { id: 'yaklasan', label: 'Yaklaşan Rezervasyonlar', path: '/yaklasan', icon: 'ri-time-line' },
  { id: 'arac-atama', label: 'Araç Atama', path: '/arac-atama', icon: 'ri-car-line' },
  { id: 'sofor-hesap', label: 'Şoför Hesap', path: '/sofor-hesap', icon: 'ri-steering-2-line' },
  { id: 'gelir-raporlari', label: 'Gelir / Ciro Raporları', path: '/gelir-raporlari', icon: 'ri-bar-chart-line' },
  { id: 'ilce-analizi', label: 'İlçe / Bölge Analizi', path: '/ilce-analizi', icon: 'ri-map-pin-line' },
  { id: 'fiyat-hesaplama', label: 'Otomatik Fiyat Hesaplama', path: '/fiyat-hesaplama', icon: 'ri-calculator-line' },
  { id: 'araclar', label: 'Araçlar', path: '/araclar', icon: 'ri-roadster-line' },
  { id: 'musteriler', label: 'Müşteriler', path: '/musteriler', icon: 'ri-user-line' },
  { id: 'ayarlar', label: 'Ayarlar', path: '/ayarlar', icon: 'ri-settings-3-line' },
];

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    setCurrentDate(new Date().toLocaleDateString('tr-TR', options));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('vip_admin_auth');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-vip-black text-white font-sans flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-vip-dark border-r border-vip-gold/10
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
      >
        {/* Logo Area */}
        <div className="p-5 border-b border-vip-gold/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/63ce3e67fd2d5effcfb662b2683f46ed/a7876054204e657e23ebb298605caa33.jpeg"
                alt="VIP Antalya Transfer"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-sm font-display font-semibold text-vip-gold leading-tight">
                VIP Antalya
              </h1>
              <p className="text-[10px] text-gray-400 leading-tight">Transfer Paneli</p>
            </div>
          </div>
        </div>

        {/* Menu */}
        <nav className="flex-1 overflow-y-auto py-3 px-3">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.id}>
                  <a
                    href={item.path}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200
                      ${isActive
                        ? 'bg-vip-gold/15 text-vip-gold border border-vip-gold/20'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }
                    `}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={item.icon}></i>
                    </div>
                    <span className="whitespace-nowrap">{item.label}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-vip-gold/10">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200 w-full"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-logout-box-line"></i>
            </div>
            <span>Çıkış Yap</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="h-16 bg-vip-dark/80 backdrop-blur-sm border-b border-vip-gold/10 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
            >
              <i className="ri-menu-line text-lg"></i>
            </button>
            <h2 className="text-lg font-display font-semibold text-white hidden sm:block">
              VIP Antalya Transfer Paneli
            </h2>
          </div>

          <div className="flex items-center gap-3 md:gap-5">
            {/* Date */}
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-400">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-vip-gold"></i>
              </div>
              <span>{currentDate}</span>
            </div>

            {/* Quick Actions */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => navigate('/rezervasyonlar/yeni')}
                className="flex items-center gap-2 px-3 py-1.5 bg-vip-gold/15 text-vip-gold text-xs font-medium rounded-md border border-vip-gold/20 hover:bg-vip-gold/25 transition-colors whitespace-nowrap"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-add-line text-xs"></i>
                </div>
                Yeni Rezervasyon
              </button>
            </div>

            {/* User Profile */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-vip-gold/20 border border-vip-gold/30 flex items-center justify-center">
                <i className="ri-user-line text-vip-gold text-sm"></i>
              </div>
              <span className="text-sm text-gray-300 hidden md:block">Admin</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}